import { createRoute } from '@tanstack/react-router';
import { rootRoute } from '@trashlab/sitesapp/src/routes/__root';
import {
  IconCircleCheck,
  IconTruck,
  IconUser,
  IconPackage,
  IconCalendar,
  IconMapPin,
  IconCurrencyDollar,
} from '@tabler/icons-react';
import { LeadTrackingInjector } from '@trashlab/sitesapp/src/organizations/redebins/components/shop/lead-tracking-injector';
import { z } from 'zod';
import currency from 'currency.js';

// Schema for the query params the API redirect handler appends after checkout.
// All fields optional since params may be absent if the order is still being created.
const thankYouParamsSchema = z.object({
  customer: z.string().optional(),
  orderNumber: z.string().optional(),
  orderId: z.string().uuid().optional(),
  orgId: z.string().uuid().optional(),
  containerSize: z.string().optional(),
  amount: z.string().optional(),
  deliveryDate: z.string().optional(),
  deliveryLocation: z.string().optional(),
  trackingUrl: z.string().url().optional(),
});

/**
 * REDEBINS-specific thank-you page at /order-received.
 * Uses /order-received path to match the customer's existing GTM trigger.
 * This gives the customer a dedicated, trackable URL for conversion analytics
 * (separate from the default /cart/$cartId/thankyou modal).
 *
 * The API redirect handler appends order details as query params:
 * customer, email, phone, containerSize, orderNumber, orderId,
 * amount, deliveryDate, deliveryLocation, trackingData.
 *
 * To activate: set the org's `onlineOrderThankYouPageUrl` to
 * https://mn.redebins.com/order-received
 */
const ThankYouPage = () => {
  const rawParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : '',
  );
  const parsed = thankYouParamsSchema.safeParse(
    Object.fromEntries(rawParams.entries()),
  );
  // Gracefully degrade: if params are malformed, just show empty state
  const params = parsed.success ? parsed.data : {};

  const {
    customer,
    orderNumber,
    orgId,
    containerSize,
    amount,
    deliveryDate,
    deliveryLocation,
    trackingUrl,
  } = params;

  // Tracking URL is generated as a shortlink by the API redirect handler
  const orderTrackingUrl = trackingUrl ?? null;
  // Customer portal link matches the nav and shop-v3 confirmation page pattern
  const customerPortalUrl = orgId
    ? `https://app.trashlab.com/customer-portal/login?orgId=${orgId}`
    : 'https://app.trashlab.com/customer-portal/login';

  // Format the delivery date for display - try-catch guards against malformed date strings
  let formattedDate: string | null = null;
  if (deliveryDate) {
    try {
      const parsed = new Date(deliveryDate + 'T00:00:00');
      if (!isNaN(parsed.getTime())) {
        formattedDate = parsed.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      }
    } catch {
      // Malformed date param - just skip displaying it
    }
  }

  return (
    <>
      <LeadTrackingInjector orderNumber={orderNumber ?? undefined} />

      <section className="py-16 md:py-24 bg-gray-50 min-h-[60vh]">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            {/* Success icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                <IconCircleCheck size={48} className="text-green-600" />
              </div>
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-[#333333] mb-4">
              Thank You{customer ? `, ${customer}` : ''}!
            </h1>

            <p className="text-lg text-gray-600 mb-4">
              Your order has been placed successfully!
            </p>

            {orderNumber && (
              <p className="text-base text-gray-500 mb-8">
                Order #{orderNumber}
              </p>
            )}

            {!orderNumber && (
              <p className="text-sm text-gray-500 mb-8">
                Your order number will be emailed to you shortly.
              </p>
            )}

            {/* Order details card */}
            {(containerSize || amount || formattedDate || deliveryLocation) && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8 text-left">
                <h2 className="text-lg font-semibold text-[#333333] mb-4">
                  Order Summary
                </h2>
                <div className="space-y-3">
                  {containerSize && (
                    <div className="flex items-center gap-3">
                      <IconPackage size={20} className="text-[#E31837] shrink-0" />
                      <span className="text-gray-700">{containerSize}</span>
                    </div>
                  )}
                  {amount && (
                    <div className="flex items-center gap-3">
                      <IconCurrencyDollar size={20} className="text-[#E31837] shrink-0" />
                      <span className="text-gray-700">{currency(amount).format()}</span>
                    </div>
                  )}
                  {formattedDate && (
                    <div className="flex items-center gap-3">
                      <IconCalendar size={20} className="text-[#E31837] shrink-0" />
                      <span className="text-gray-700">{formattedDate}</span>
                    </div>
                  )}
                  {deliveryLocation && (
                    <div className="flex items-center gap-3">
                      <IconMapPin size={20} className="text-[#E31837] shrink-0" />
                      <span className="text-gray-700">{deliveryLocation}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Action links */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {orderTrackingUrl && (
                <a
                  href={orderTrackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#E31837] text-white font-semibold rounded-lg hover:bg-[#c41530] transition-colors"
                >
                  <IconTruck size={20} />
                  Track Your Order
                </a>
              )}
              <a
                href={customerPortalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#E31837] text-[#E31837] font-semibold rounded-lg hover:bg-[#E31837] hover:text-white transition-colors"
              >
                <IconUser size={20} />
                Manage My Account
              </a>
            </div>

            <p className="text-sm text-gray-500 mt-8">
              You will receive a confirmation email shortly with all the
              details. If you have any questions, feel free to{' '}
              <a href="/contact" className="text-[#E31837] underline">
                contact us
              </a>
              .
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/order-received',
  component: ThankYouPage,
});
